import { i18n } from '@/i18n';

import {
	GET_NAVIGATION_ITEM_LIST_REQUEST,
	GET_NAVIGATION_ITEM_LIST_SUCCESS,
	GET_NAVIGATION_ITEM_LIST_FAILURE,
	GET_NAVIGATION_ITEM_REQUEST,
	GET_NAVIGATION_ITEM_SUCCESS,
	GET_NAVIGATION_ITEM_FAILURE,
	CREATE_NAVIGATION_ITEM_REQUEST,
	CREATE_NAVIGATION_ITEM_SUCCESS,
	CREATE_NAVIGATION_ITEM_FAILURE,
	UPDATE_NAVIGATION_ITEM_REQUEST,
	UPDATE_NAVIGATION_ITEM_SUCCESS,
	UPDATE_NAVIGATION_ITEM_FAILURE,
	DELETE_NAVIGATION_ITEM_REQUEST,
	DELETE_NAVIGATION_ITEM_SUCCESS,
	DELETE_NAVIGATION_ITEM_FAILURE,
} from '../types';

import {
	getNavigationItemsAPI,
	getNavigationItemAPI,
	postNavigationItemAPI,
	updateNavigationItemAPI,
	deleteNavigationItemAPI,
} from '../../services/api/navigationItem.api';

import {
	transformedNavigationMenuItemList,
	transformedNavigationMenuToApi,
	transformedNavigationMenuItemDetail,
} from '../../assets/js/transform/navigationMenu';

import { transformedMeta } from '../../assets/js/transform/meta';

export default {
	namespaced: true,
	state: {
		create: {
			isCreating: false,
		},
		edit: {
			data: {},
			isError: false,
			isLoading: false,
			isUpdating: false,
			isDeleting: false,
		},
		list: {
			isLoading: false,
			data: [],
			meta: {},
			isError: false,
		},
	},
	getters: {},
	mutations: {
		[GET_NAVIGATION_ITEM_REQUEST](state) {
			state.edit.isLoading = true;
			state.edit.isError = false;
		},
		[GET_NAVIGATION_ITEM_SUCCESS](state, { data }) {
			state.edit.data = transformedNavigationMenuItemDetail(data);
			state.edit.isLoading = false;
			state.edit.isError = false;
		},
		[GET_NAVIGATION_ITEM_FAILURE](state) {
			state.edit.isLoading = false;
			state.edit.isError = true;
		},
		[GET_NAVIGATION_ITEM_LIST_REQUEST](state, isAppend = false) {
			state.list.isLoading = true;
			if (!isAppend) {
				state.list.data = [];
				state.list.meta = {};
			}
			state.list.isError = false;
		},
		[GET_NAVIGATION_ITEM_LIST_SUCCESS](state, { data = [], meta = {}, isAppend = false }) {
			state.list.isLoading = false;
			state.list.isError = false;
			const transformedData = transformedNavigationMenuItemList(data);
			state.list.data = isAppend ? [...state.list.data, ...transformedData] : transformedData;
			state.list.meta = transformedMeta(meta);
		},
		[GET_NAVIGATION_ITEM_LIST_FAILURE](state) {
			state.list.data = [];
			state.list.meta = {
				total: 0,
				lastPage: 1,
				currentPage: 1,
			};
			state.list.isLoading = false;
			state.list.isError = true;
		},
		[CREATE_NAVIGATION_ITEM_REQUEST](state) {
			state.create.isCreating = true;
		},
		[CREATE_NAVIGATION_ITEM_SUCCESS](state) {
			state.create.isCreating = false;
		},
		[CREATE_NAVIGATION_ITEM_FAILURE](state) {
			state.create.isCreating = false;
		},
		[UPDATE_NAVIGATION_ITEM_REQUEST](state) {
			state.edit.isUpdating = true;
		},
		[UPDATE_NAVIGATION_ITEM_SUCCESS](state) {
			state.edit.isUpdating = false;
		},
		[UPDATE_NAVIGATION_ITEM_FAILURE](state) {
			state.edit.isUpdating = false;
		},
		[DELETE_NAVIGATION_ITEM_REQUEST](state) {
			state.edit.isDeleting = true;
		},
		[DELETE_NAVIGATION_ITEM_SUCCESS](state) {
			state.edit.isDeleting = false;
		},
		[DELETE_NAVIGATION_ITEM_FAILURE](state) {
			state.edit.isDeleting = false;
		},
	},
	actions: {
		/**
		* Get navigation items
		* @param {Object} params { page, platform, content_type }
		* @return {Array} array of navigation item
		*/
		async getNavigationItems({ commit }, params) {
			try {
				commit(GET_NAVIGATION_ITEM_LIST_REQUEST, params.isAppend);
				const { data } = await getNavigationItemsAPI(params);
				commit(GET_NAVIGATION_ITEM_LIST_SUCCESS, {
					data: data.data,
					meta: data.meta,
					isAppend: params.isAppend,
				});
			} catch (error) {
				commit(GET_NAVIGATION_ITEM_LIST_FAILURE);
			}
		},
		async getNavigationItem({ commit }, id) {
			try {
				commit(GET_NAVIGATION_ITEM_REQUEST);
				const { data } = await getNavigationItemAPI(id);
				commit(GET_NAVIGATION_ITEM_SUCCESS, { data: data.data });
			} catch (error) {
				commit(GET_NAVIGATION_ITEM_FAILURE);
			}
		},
		async createNavigationItem({ commit, dispatch }, { params, contentType } = {}) {
			try {
				const newParams = {
					...params,
					contentType,
				};
				commit(CREATE_NAVIGATION_ITEM_REQUEST, newParams);
				await postNavigationItemAPI(transformedNavigationMenuToApi(newParams));
				commit(CREATE_NAVIGATION_ITEM_SUCCESS);
				dispatch(
					'toast/showCreateSuccessToast',
					i18n.t('page.navigationItem.title'),
					{ root: true },
				);
			} catch (error) {
				commit(CREATE_NAVIGATION_ITEM_FAILURE);
				dispatch('toast/showCreateErrorToast', error, { root: true });
				throw error;
			}
		},
		async updateNavigationItem({ commit, dispatch }, { params, contentType } = {}) {
			try {
				const newParams = {
					...params,
					contentType,
				};
				commit(UPDATE_NAVIGATION_ITEM_REQUEST, newParams);
				await updateNavigationItemAPI(newParams.id, transformedNavigationMenuToApi(newParams));
				commit(UPDATE_NAVIGATION_ITEM_SUCCESS);
				dispatch('toast/showUpdateSuccessToast', null, { root: true });
			} catch (error) {
				commit(UPDATE_NAVIGATION_ITEM_FAILURE);
				const statusCode = error?.response?.status ?? null;
				if (statusCode !== 422) {
					dispatch('toast/showUpdateErrorToast', error, { root: true });
				}
				throw error;
			}
		},
		async deleteNavigationItem({ commit, dispatch }, id) {
			try {
				commit(DELETE_NAVIGATION_ITEM_REQUEST);
				await deleteNavigationItemAPI(id);
				commit(DELETE_NAVIGATION_ITEM_SUCCESS);
				dispatch(
					'toast/showDeleteSuccessToast',
					i18n.t('page.navigationItem.title'),
					{ root: true },
				);
			} catch (error) {
				commit(DELETE_NAVIGATION_ITEM_FAILURE);
				dispatch('toast/showDeleteErrorToast', error, { root: true });
				throw error;
			}
		},
	},
};
